// Recently Viewed Products Alpine.js Component

async function enrichProductsWithFeatureBadge(products) {
    if (!products?.length) return products || [];

    return Promise.all(
        products.map(async (product) => {
            const handle = product.handle;
            if (!handle) return product;

            try {
                const response = await fetch(`/products/${handle}?view=card-meta`);
                if (response.ok) {
                    const data = await response.json();
                    return { ...product, featureBadge: data.featureBadge || null };
                }
            } catch (error) {
                // Silent fail — badge is optional
            }

            return product;
        })
    );
}

document.addEventListener('alpine:init', () => {
    Alpine.data('recentlyViewed', () => ({
        products: [],
        isLoading: true,
        maxProducts: 8,
        currencySymbol: '$',
        moneyFormat: '${{amount}}',

        init() {
            this.loadRecentlyViewed();
            this.trackCurrentProduct();
        },



        loadRecentlyViewed() {
            const recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewedProducts') || '[]');
            
            if (recentlyViewed.length === 0) {
                this.isLoading = false;
                return;
            }

            // Fetch products directly using Shopify's product.js endpoint
            this.fetchRecentlyViewedProducts(recentlyViewed.slice(0, this.maxProducts));
        },

        async fetchRecentlyViewedProducts(productHandles) {
            const promises = productHandles.map(productHandle => 
                fetch(`/products/${productHandle}.js`)
                    .then(response => {
                        if (!response.ok) return null;
                        return response.json();
                    })
                    .catch(() => null)
            );

            const products = await Promise.all(promises);

            const enrichedProducts = await enrichProductsWithFeatureBadge(
                products.filter(product => product !== null)
            );

            this.products = enrichedProducts.map(product => ({
                ...product,
                url: product.url || `/products/${product.handle}`
            }));
            this.isLoading = false;
        },



        trackCurrentProduct() {
            if (window.location.pathname.includes('/products/')) {
                const productHandle = window.location.pathname.split('/products/')[1].split('?')[0];
                let recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewedProducts') || '[]');
                
                // Remove if already exists and add to beginning
                recentlyViewed = recentlyViewed.filter(handle => handle !== productHandle);
                recentlyViewed.unshift(productHandle);
                
                // Keep only last 12 items
                recentlyViewed = recentlyViewed.slice(0, 12);
                
                localStorage.setItem('recentlyViewedProducts', JSON.stringify(recentlyViewed));
            }
        },

        formatPrice(price) {
            // Check if Shopify.formatMoney is available, otherwise fallback to simple formatting
            if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
                return Shopify.formatMoney(price, this.moneyFormat);
            }
            // Fallback formatting using store's currency symbol
            const amount = (price / 100).toFixed(2);
            const amountWithComma = (price / 100).toFixed(2).replace('.', ','); // EU format uses comma as decimal separator
            const amountNoDecimals = Math.round(price / 100);
            
            return this.moneyFormat
                .replace('{{amount}}', amount)
                .replace('{{amount_with_comma_separator}}', amountWithComma)
                .replace('{{amount_no_decimals}}', amountNoDecimals)
                .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.toString().replace('.', ','));
        },

        getProductUrl(handle) {
            return `/products/${handle}`;
        },

        getImageUrl(product) {
            if (product.featured_image) {
                return product.featured_image.replace(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?.*)?$/i, '_1000x.$1$2');
            }
            return '';
        },

        getTitleParts(title) {
            return title.split('-')[0].trim();
        },

        formatTitle(title) {
            if (!title) return '';
            
            // Handle both " - " (space-dash-space) and "-" (dash) separators
            const titleParts = title.includes(' - ') 
                ? title.split(' - ')
                : title.split('-');
                
            if (titleParts.length > 1) {
                // Take all parts except the last one as the base product name
                const separator = title.includes(' - ') ? ' - ' : '-';
                const baseTitle = titleParts.slice(0, -1).join(separator);
                const color = titleParts[titleParts.length - 1].trim();
                return `${baseTitle} | ${color}`;
            }
            
            return title;
        },

        getColourName(product) {
            // First check if we have a direct colorName property (from our new Liquid template)
            if (product.colorName) {
                return product.colorName;
            }
            
            // Try to get color from metafields
            if (product.metafields && product.metafields.custom && product.metafields.custom.colour) {
                const colourMetafield = product.metafields.custom.colour;
                
                // Check the nested structure from our Liquid template
                if (colourMetafield.value && colourMetafield.value.name && colourMetafield.value.name.value) {
                    return colourMetafield.value.name.value;
                }
            }
            
            // Fallback to extracting from product title
            if (product.title && product.title.includes('-')) {
                const parts = product.title.split('-');
                if (parts.length > 1) {
                    return parts[parts.length - 1].trim();
                }
            }
            
            return '';
        },

        /**
         * Translates badge text to consistent format matching locale translations
         */
        translateBadgeText(badgeText) {
            const badgeMap = {
                'preorder': 'Pre-order',
                'pre_order': 'Pre-order',
                'new': 'New',
                'new_in': 'New In',
                'newin': 'New In',
                'new_arrival': 'New Arrival',
                'newinarrival': 'New Arrival',
                'new_in_arrival': 'New Arrival',
                'sale': 'Sale',
                'limited': 'Limited',
                'exclusive': 'Exclusive',
                'bestseller': 'Bestseller',
                'best_seller': 'Bestseller',
                'archive': 'Archive'
            };
            
            const badgeTextLower = badgeText.toLowerCase().replace(/[- ]/g, '_');
            return badgeMap[badgeTextLower] || badgeText;
        },

        getProductBadges(product) {
            const badges = [];
            
            // Ensure tags is an array
            if (!product.tags || !Array.isArray(product.tags)) {
                return badges;
            }
            
            const hasArchive = product.tags.includes('Archive');
            const hasPreorder = product.tags.includes('preorder');
            const hasSaleBadge = product.tags.some(tag => tag.includes('badge-sale:'));

            // Priority: archive > sale > preorder > new
            // If archive exists, only show archive badge
            if (hasArchive) {
                const archiveText = this.translateBadgeText('archive');
                return [{ type: 'archive', text: archiveText }];
            }

            // If sale badge exists, only show sale badge
            if (hasSaleBadge) {
                const saleTag = product.tags.find(tag => tag.includes('badge-sale:'));
                if (saleTag) {
                    const saleBadgeText = saleTag.split('badge-sale:')[1];
                    const translatedText = this.translateBadgeText(saleBadgeText);
                    return [{ type: 'sale', text: translatedText }];
                }
            }

            // If preorder exists, only show preorder badge
            if (hasPreorder) {
                const preorderText = this.translateBadgeText('preorder');
                return [{ type: 'preorder', text: preorderText }];
            }

            // Otherwise, build badges from `badge-primary:` tags (new, etc.)
            product.tags.forEach((tag) => {
                if (tag.includes('badge-primary:')) {
                    const badgeText = tag.split('badge-primary:')[1];
                    if (badgeText) {
                        badges.push({ type: 'primary', text: this.translateBadgeText(badgeText) });
                    }
                }
            });

            return badges;
        }
    }));
}); 